import React, { useState, useImperativeHandle, forwardRef, useEffect } from 'react';
import SegmentedButtons from '@gumtree/ui-library-v2/src/segmented-buttons/segmented-buttons';
import { LeadFormItemProps, LeadFormItemErrorMessages } from './LeadFormItem.type';
import {
    ItemContainer,
    Label,
    ErrorText,
    InputBox,
    SelectBox,
    TextAreaBox,
    SegmentedButtonsWrapper,
} from './LeadFormItem.style';

const LeadFormItem = forwardRef<any, LeadFormItemProps>((props, ref) => {
    const {
        type,
        label,
        name,
        required,
        requiredLabel = false,
        maxLength,
        minLength,
        pattern,
        validator,
        errorMessage,
        options = [],
        value: propValue,
        onChange,
        placeholder,
        disabled,
    } = props;

    const [value, setValue] = useState<string | number>(propValue || '');
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        setValue(propValue || '');
    }, [propValue]);

    const getErrorMessage = (type: 'required' | 'minLength' | 'pattern'): string => {
        if (typeof errorMessage === 'object' && errorMessage !== null) {
            const errorMessages = errorMessage as LeadFormItemErrorMessages;
            switch (type) {
                case 'required':
                    return errorMessages.required || 'This field is required';
                case 'minLength':
                    return errorMessages.minLength || `Minimum ${minLength} characters required`;
                case 'pattern':
                    return errorMessages.pattern || 'Invalid format';
                default:
                    return 'Invalid input';
            }
        }

        if (typeof errorMessage === 'string') {
            return errorMessage;
        }

        switch (type) {
            case 'required':
                return 'This field is required';
            case 'minLength':
                return `Minimum ${minLength} characters required`;
            case 'pattern':
                return 'Invalid format';
            default:
                return 'Invalid input';
        }
    };

    const doValidate = () => {
        if (required && !value) {
            setError(getErrorMessage('required'));
            return false;
        }
        if (minLength && value && String(value).length < minLength) {
            setError(getErrorMessage('minLength'));
            return false;
        }
        if (pattern && value && !pattern.test(String(value))) {
            setError(getErrorMessage('pattern'));
            return false;
        }
        if (validator && value) {
            const validationResult = validator(value);
            if (typeof validationResult === 'string') {
                setError(validationResult);
                return false;
            } else if (validationResult === false) {
                setError('Invalid input');
                return false;
            }
        }
        setError(null);
        return true;
    };

    useImperativeHandle(ref, () => ({
        validate: doValidate,
        getValue: () => value,
        setValue: (v: string | number) => setValue(v),
        setError: (msg: string) => setError(msg),
        clearError: () => setError(null),
        props,
    }));

    const handleChange = (v: string | number) => {
        setValue(v);
        setError(null);
        onChange?.(v);
    };

    const handleBlur = () => {
        doValidate();
    };

    return (
        <ItemContainer isSegmented={type === 'segmented'}>
            <Label error={!!error} isSegmented={type === 'segmented'}>
                {label}
                {required && requiredLabel && ' *'}
            </Label>
            {type === 'input' && (
                <InputBox
                    name={name}
                    value={value}
                    onChange={(e) => handleChange(e.target.value)}
                    onBlur={handleBlur}
                    maxLength={maxLength}
                    minLength={minLength}
                    placeholder={placeholder}
                    disabled={disabled}
                    error={!!error}
                />
            )}
            {type === 'textarea' && (
                <TextAreaBox
                    name={name}
                    value={value}
                    onChange={(e) => handleChange(e.target.value)}
                    onBlur={handleBlur}
                    maxLength={maxLength}
                    minLength={minLength}
                    placeholder={placeholder}
                    disabled={disabled}
                    error={!!error}
                />
            )}
            {type === 'select' && (
                <SelectBox
                    name={name}
                    value={value}
                    onChange={(e) => handleChange(e.target.value)}
                    onBlur={handleBlur}
                    disabled={disabled}
                    error={!!error}
                >
                    <option value="" disabled>
                        Select an option
                    </option>
                    {options.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                            {opt.label}
                        </option>
                    ))}
                </SelectBox>
            )}
            {type === 'segmented' && (
                <SegmentedButtonsWrapper>
                    <SegmentedButtons
                        options={options}
                        selectedOption={value}
                        updateSelectedOption={handleChange}
                        dataQ={name}
                    />
                </SegmentedButtonsWrapper>
            )}
            {error && <ErrorText isSegmented={type === 'segmented'}>{error}</ErrorText>}
        </ItemContainer>
    );
});

LeadFormItem.displayName = 'LeadFormItem';

export default LeadFormItem;
